import { endpoints } from '@/endpoints';
import { DBImportType } from '@/types/import';
import { DBExportType } from '@/types/export';
import _ from 'lodash';
import { Field } from '@/types/misc';
import type { RequestScope } from '@/session/scopedTransport';
import type { ReadRequestOptions } from '@/endpoints/requestOptions';

type RequestParams = {
  importType?: DBImportType;
  exportType?: DBExportType;
  subscriptions?: { id: number }[];
  scope?: RequestScope;
};

export type FieldValueType = 'text' | 'number' | 'boolean' | 'date' | 'unknown';

export type FieldMetadata = Field & {
  fieldName: string;
  valueType: FieldValueType;
};

type Response = {
  fieldNames: string[];
  fields: FieldMetadata[];
};

const numberFieldNames = new Set([
  'id',
  'allocated',
  'cost',
  'fulfillment_provider_id',
  'inventory_id',
  'not_available',
  'on_hand',
  'posting_template_id',
  'price',
  'price.amount',
  'pricing_item.price.amount',
  'pricing_item.msrp.amount',
  'pricing_item_id',
  'product_id',
  'quantity',
  'subscription_id',
  'warehouse_id',
]);

const dateFieldNames = new Set([
  'created_at',
  'updated_at',
  'date_received',
  'expiration_date',
  'date_start',
  'date_end',
  'deleted_at',
  'discontinued_at',
]);

// Kit components have a fixed import/export schema and do not support custom
// fields, so no model-fields request is made. Keep this list aligned with the
// logical import and export columns in astur_kitchensink.
const kitComponentFieldNames = [
  'id',
  'kit_sku',
  'kit_product_id',
  'kit_configuration_id',
  'sku',
  'product_id',
  'quantity',
  'inventory_floor',
] as const;

const normalizeField = (field: Field): Field => ({
  ...field,
  valid_unit_values: field.valid_unit_values ?? null,
  is_custom: Boolean(field.is_custom),
});

const getNormalizedFieldName = (field: Field) => (field.is_custom ? `fields.${field.name}` : field.name);

const inferFieldValueType = (field: Field, fieldName = getNormalizedFieldName(field)): FieldValueType => {
  const metaFieldType = field.meta_field_type;
  if (metaFieldType === 'boolean') return 'boolean';
  if (metaFieldType === 'number_integer' || metaFieldType === 'number_decimal' || metaFieldType === 'number_field') return 'number';
  if (metaFieldType === 'date' || metaFieldType === 'date_time') return 'date';
  if (metaFieldType) return 'text';

  if (numberFieldNames.has(fieldName) || fieldName.endsWith('_id') || fieldName.endsWith('.amount')) return 'number';
  if (dateFieldNames.has(fieldName) || fieldName.endsWith('_at') || fieldName.endsWith('_date')) return 'date';
  if (fieldName.startsWith('is_') || fieldName.startsWith('has_')) return 'boolean';
  return 'text';
};

const toFieldMetadata = (field: Field): FieldMetadata => {
  const fieldName = getNormalizedFieldName(field);
  return {
    ...field,
    fieldName,
    valueType: inferFieldValueType(field, fieldName),
  };
};

export const getFieldNames = async (
  { importType, exportType, subscriptions = [], scope }: RequestParams,
  options: ReadRequestOptions
): Promise<Response> => {
  if ((importType || exportType) && !scope) {
    throw new Error('Field requests require an explicit organization scope');
  }

  const value = exportType || importType;
  const isKitComponent = value === 'kit_component';
  let fields: Field[];

  if (isKitComponent) {
    fields = kitComponentFieldNames.map((name) => normalizeField({ name, is_custom: false, valid_unit_values: null }));
  } else {
    const response = exportType
      ? await endpoints.exports.getExportTypeFields(
          scope!,
          {
            exportType,
            limit: 1000,
            all: true,
          },
          options
        )
      : importType
        ? await endpoints.imports.getImportTypeFields(
            scope!,
            {
              importType,
              limit: 1000,
              all: true,
            },
            options
          )
        : null;

    if (!response || response.isError || !Array.isArray(response.result)) {
      throw new Error(response?.errors?.error ?? 'Failed to get fields');
    }

    fields = response.result.map(normalizeField);
  }

  if (Array.isArray(fields)) {
    if (!isKitComponent) fields.unshift({ name: 'id', is_custom: false, valid_unit_values: null });

    if (value === 'catalog') {
      const topLevelArrays = ['options', 'group_skus'];
      for (let i = 0; i < 20; i++) {
        topLevelArrays.forEach((array) => {
          fields.push({ name: array + '.' + i, is_custom: false, valid_unit_values: null });
        });
      }
      fields.push({ name: 'images.default.main.url', is_custom: false, valid_unit_values: null });
      fields.push({ name: 'images.default.swatch.url', is_custom: false, valid_unit_values: null });
      for (let i = 1; i < 20; i++) {
        fields.push({ name: 'images' + '.default.' + i + '.alternate.url', is_custom: false, valid_unit_values: null });
      }
      fields.unshift({ name: 'pricing_item.price.amount', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'pricing_item.price.currency', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'pricing_item.msrp.amount', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'pricing_item.msrp.currency', is_custom: false, valid_unit_values: null });

      subscriptions.forEach((subscription) => {
        fields.push({ name: 'sku_presentation.' + subscription.id + '.sku', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'sku_presentation.' + subscription.id + '.isbn', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'sku_presentation.' + subscription.id + '.ean', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'sku_presentation.' + subscription.id + '.gtin', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'sku_presentation.' + subscription.id + '.mpn', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'sku_presentation.' + subscription.id + '.upc', is_custom: false, valid_unit_values: null });
      });

      topLevelArrays.push('images');
      fields = fields.filter((field) => {
        return !topLevelArrays.includes(field.name);
      });

      const removeFields = ['pricing_item', 'group_ids', 'variants', 'variant_options', 'sku_presentation'];

      fields = fields.filter((field) => {
        return !removeFields.includes(field.name);
      });
    }
    if (value === 'custom_pricing') {
      fields.unshift({ name: 'price.amount', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'price.currency', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'msrp.amount', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'msrp.currency', is_custom: false, valid_unit_values: null });

      const removeFields = ['pricing_item', 'mapp', 'msrp', 'price', 'id'];

      fields = fields.filter((field) => {
        return !removeFields.includes(field.name);
      });
    }
    if (value === 'promotion') {
      fields.unshift({ name: 'price.amount', is_custom: false, valid_unit_values: null });
      fields.unshift({ name: 'price.currency', is_custom: false, valid_unit_values: null });

      const removeFields = ['pricing_item_id', 'price'];

      fields = fields.filter((field) => {
        return !removeFields.includes(field.name);
      });
    }

    if (value === 'inventory_detail') {
      fields = [];
      fields.push({ name: 'sku', is_custom: false, valid_unit_values: null });
      fields.push({ name: 'warehouse_id', is_custom: false, valid_unit_values: null });
      fields.push({ name: 'quantity', is_custom: false, valid_unit_values: null });
      fields.push({ name: 'allocated', is_custom: false, valid_unit_values: null });
    }

    if (value === 'sku_presentation') {
      subscriptions.forEach((subscription) => {
        fields.push({ name: 'subscription_' + subscription.id + '.sku', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'subscription_' + subscription.id + '.isbn', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'subscription_' + subscription.id + '.ean', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'subscription_' + subscription.id + '.gtin', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'subscription_' + subscription.id + '.mpn', is_custom: false, valid_unit_values: null });
        fields.push({ name: 'subscription_' + subscription.id + '.upc', is_custom: false, valid_unit_values: null });
      });
    }

    fields = fields.flatMap((field) => {
      if (field.valid_unit_values && field.valid_unit_values.length > 0) {
        return [
          { name: field.name + '.value', is_custom: true, valid_unit_values: null, meta_field_type: 'number_decimal' },
          { name: field.name + '.unit', is_custom: true, valid_unit_values: null, meta_field_type: 'single_line_text_field' },
        ];
      }
      return [field];
    });

    fields = _.uniqBy(fields, 'name');

    const customFields = fields.filter((field) => field.is_custom);
    const nonCustomFields = fields.filter((field) => !field.is_custom);

    customFields.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    nonCustomFields.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    fields = [...nonCustomFields, ...customFields];
  }

  const fieldMetadata = fields.map(toFieldMetadata);
  const fieldNames = fieldMetadata.map((field) => field.fieldName);

  return {
    fieldNames,
    fields: fieldMetadata,
  };
};
